import * as React from "react";
import { WizardCarPWAProps, WizardCarPWAState } from "./typings";
import { inject, observer } from "mobx-react";
import { WizardCarPWAView } from "./WizardCarPWAView";
import { useLocalization } from "@shared-ui/localization-context";
import { locKeys } from "./components/l10n";
import { Experiment, isVariantEnabled } from "../../utility/experiment";
import { carSubLOBs } from "../../../stores/wizard/state/typings";

@inject("wizardState", "routeStore")
@observer
export class WizardCarPWA extends React.Component<WizardCarPWAProps, WizardCarPWAState> {
  constructor(props: WizardCarPWAProps) {
    super(props);
    this.state = {
      isSubLOBMenuOpen: false,
      tabSelected: this.getTabState(),
      isDiffDropOff: false,
      isDriverAgeInRange: true,
      isAARPChecked: false,
    };

    const { carWizardState } = this.props.wizardState;
    carWizardState.tabSelected = this.getTabState();
    carWizardState.overrideConfig(() => {
      carWizardState.config.elementId = this.props.flexComponent.id;
      carWizardState.config.heading.fixedTitle = this.props.flexComponent.model.fixedLobTitle;
      carWizardState.config.lastSearch.useLastSearchForSingleLOB = this.props.flexComponent.model.useLastSearchForSingleLOB;
      carWizardState.config.heading.lobSubtitleBreakPoint = this.props.flexComponent.model.lobSubtitleBreakPoint;
      carWizardState.config.heading.hideLobTitle = this.props.flexComponent.model.hideLobTitle;
      carWizardState.config.includeGT = this.props.flexComponent.model.includeGT ?? carWizardState.config.includeGT;
      carWizardState.config.simplifiedGroundTransportationForm =
        this.props.flexComponent.model.simplifyGroundTransportationForm ??
        carWizardState.config.simplifiedGroundTransportationForm;
      const addGoogleFallback = (essFeatures: string): string => {
        if (
          isVariantEnabled(
            this.props.context,
            Experiment.Typeahead_Enabling_Google_Autocomplete_Suggestion_for_Car_Lob
          ) &&
          essFeatures.indexOf("google") < 0
        ) {
          essFeatures += "|google";
        }

        return essFeatures;
      };
      const essFeaturesDest = carWizardState.config.location.destination.essAdapterConfig.features;
      const essFeaturesOrigin = carWizardState.config.location.origin.essAdapterConfig.features;
      carWizardState.config.location.destination.essAdapterConfig.features = addGoogleFallback(essFeaturesDest);
      carWizardState.config.location.origin.essAdapterConfig.features = addGoogleFallback(essFeaturesOrigin);
    });
    carWizardState.updateStateFromCookie();
    carWizardState.updateDateFromConfig();
  }

  public componentDidMount() {
    this.setState(() => ({
      isDiffDropOff: this.props.wizardState.carWizardState.isDifferentDropOff,
    }));
  }

  public componentDidUpdate() {
    const pwaSubLOB = this.props.routeStore.queryParams.pwaSubLOB;
    if (pwaSubLOB !== carSubLOBs.to) {
      const wizardCarState = this.props.wizardState.carWizardState;

      const currentPwaSubLOB = this.getCurrentPwaSubLOB();
      const activeSubLOB = this.getActivePwaSubLOB();

      if (currentPwaSubLOB !== activeSubLOB) {
        wizardCarState.toggleTabSelected();

        this.setState(({ tabSelected }) => ({
          tabSelected: !tabSelected,
        }));
      }
      wizardCarState.updateSubLOBState(currentPwaSubLOB);
    }
  }

  private getActiveGroundTransportationSubLOB = () =>
    this.props.wizardState.carWizardState.config.simplifiedGroundTransportationForm
      ? carSubLOBs.groundTrasportation
      : carSubLOBs.from;

  private getActivePwaSubLOB = () => {
    const { tabSelected } = this.props.wizardState.carWizardState;

    return tabSelected ? carSubLOBs.rental : this.getActiveGroundTransportationSubLOB();
  };

  private getCurrentPwaSubLOB = () => {
    const queryParams = location.search;
    if (queryParams) {
      return queryParams.includes(this.getActiveGroundTransportationSubLOB())
        ? this.getActiveGroundTransportationSubLOB()
        : carSubLOBs.rental;
    }

    return this.getActivePwaSubLOB();
  };

  private getTabState = () => {
    const { pwaSubLOB } = this.props.routeStore.queryParams;

    return pwaSubLOB !== this.getActiveGroundTransportationSubLOB();
  };

  private toggleSubLOBMenu = () => {
    this.setState(({ isSubLOBMenuOpen }) => ({
      isSubLOBMenuOpen: !isSubLOBMenuOpen,
    }));
  };

  private toggleTabOptions = () => {
    const wizardCarState = this.props.wizardState.carWizardState;
    wizardCarState.toggleTabSelected();
    this.setState(({ tabSelected }) => ({
      tabSelected: !tabSelected,
    }));

    const pwaSubLOB = this.getActivePwaSubLOB();
    this.props.routeStore.updateSearch({ pwaSubLOB });
  };

  private toggleDifferentDropOff = () => {
    this.setState(({ isDiffDropOff }) => ({
      isDiffDropOff: !isDiffDropOff,
    }));
    this.props.wizardState.carWizardState.toggleIsDifferentDropOff();
  };

  private toggleDriverAgeInRange = () => {
    this.setState(({ isDriverAgeInRange }) => ({
      isDriverAgeInRange: !isDriverAgeInRange,
    }));
  };

  private onAARPChange = () => {
    this.setState(({ isAARPChecked }) => ({
      isAARPChecked: !isAARPChecked,
    }));
  };

  private toggleBookRoundtrip = () => this.props.wizardState.carWizardState.toggleBookRoundtrip();

  public onSubLOBMenuTriggerClick = () => this.toggleSubLOBMenu();

  public onTabOptionSelected = () => this.toggleTabOptions();

  public onDifferentDropOffSelected = () => this.toggleDifferentDropOff();

  public onDriverAgeInRange = () => this.toggleDriverAgeInRange();

  public onSubLOBMenuItemClick = (pwaSubLOB: string) => () => {
    this.toggleSubLOBMenu();
    this.props.routeStore.updateSearch({ pwaSubLOB });
  };

  private setType = () => {
    const { formatText } = useLocalization();
    const pwaSubLOB = this.props.routeStore.queryParams.pwaSubLOB;

    if (!pwaSubLOB) {
      return formatText(locKeys.gtSubLOBFrom);
    }

    this.props.wizardState.carWizardState.updateSubLOBState(pwaSubLOB);

    return formatText(pwaSubLOB === carSubLOBs.from ? locKeys.gtSubLOBFrom : locKeys.gtSubLOBTo);
  };

  public render() {
    return (
      <WizardCarPWAView
        {...this.props}
        setType={this.setType}
        onTabOptionSelected={this.onTabOptionSelected}
        toggleBookRoundtrip={this.toggleBookRoundtrip}
        onSubLOBMenuItemClick={this.onSubLOBMenuItemClick}
        onSubLOBMenuTriggerClick={this.onSubLOBMenuTriggerClick}
        onDifferentDropOffSelected={this.onDifferentDropOffSelected}
        onDriverAgeInRange={this.onDriverAgeInRange}
        onAARPChange={this.onAARPChange}
        isDiffDropOff={this.state.isDiffDropOff}
        isDriverAgeInRange={this.state.isDriverAgeInRange}
        isAARPChecked={this.state.isAARPChecked}
        isSubLOBMenuOpen={this.state.isSubLOBMenuOpen}
        tabSelected={this.state.tabSelected}
      />
    );
  }
}

export default WizardCarPWA;
